import { useEffect, useEffectEvent, useRef, useState } from 'react';
import {
  createEndlessProcess,
  removePieces,
  revealNextWave,
  type EndlessProcessState
} from './model';
import { LEAVE_MS } from './ProcessPieceView';

const WAVE_MS = 620;
const FINISH_WAVE_MS = 120;
// Long enough for the end event to spring in and every task to fill.
const SETTLE_MS = 400;
// Removing a step only after its leave animation has surely ended avoids a one-frame vanish.
const REMOVE_AFTER_MS = LEAVE_MS + 100;
const CAMERA_TIME_CONSTANT_MS = 270;

/**
 * Grows the process one wave at a time until `done`; then reveals what is left of an open
 * split quickly, draws the end event, lets it settle and calls `onFinished`.
 */
export function useEndlessProcess(done: boolean, onFinished: (() => void) | undefined) {
  const [process, setProcess] = useState(createEndlessProcess);
  const [firstFastWave, setFirstFastWave] = useState(Number.POSITIVE_INFINITY);
  const processRef = useRef(process);
  const finish = useEffectEvent(() => onFinished?.());

  useEffect(
    function revealWavesUntilFinished() {
      const timers = new Set<number>();
      const schedule = (ms: number, run: () => void) => {
        const id = window.setTimeout(() => {
          timers.delete(id);
          run();
        }, ms);
        timers.add(id);
      };
      const commit = (next: EndlessProcessState) => {
        processRef.current = next;
        setProcess(next);
      };
      const removeLater = (ids: number[]) =>
        schedule(REMOVE_AFTER_MS, () => commit(removePieces(processRef.current, ids)));
      const revealWave = () => {
        const { state: next, leavingIds } = revealNextWave(processRef.current, Math.random, done);
        commit(next);
        if (done) setFirstFastWave((wave) => Math.min(wave, next.lastWave));
        if (leavingIds.length > 0) removeLater(leavingIds);
        if (next.ended) schedule(SETTLE_MS, finish);
        else schedule(done ? FINISH_WAVE_MS : WAVE_MS, revealWave);
      };
      // A restart cancels the removals of steps that were still leaving.
      if (processRef.current.leavingIds.length > 0) removeLater(processRef.current.leavingIds);
      schedule(done ? FINISH_WAVE_MS : WAVE_MS, revealWave);
      return () => timers.forEach((id) => window.clearTimeout(id));
    },
    [done]
  );

  return { process, firstFastWave };
}

/** Glides a `<g>` towards `targetX` with a frame-rate independent lerp. */
export function useCameraFollow(targetX: number) {
  const cameraRef = useRef<SVGGElement>(null);
  const readTarget = useEffectEvent(() => targetX);

  useEffect(function glideTowardsNewestStep() {
    let frame = 0;
    let currentX = 0;
    let previousTime: number | undefined;
    const tick = (time: number) => {
      const elapsed = previousTime === undefined ? 0 : time - previousTime;
      previousTime = time;
      currentX += (readTarget() - currentX) * (1 - Math.exp(-elapsed / CAMERA_TIME_CONSTANT_MS));
      cameraRef.current?.setAttribute('transform', `translate(${-currentX} 0)`);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return cameraRef;
}
